import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wifi, WifiOff, Router } from "lucide-react";
import { SCENES } from "./apData";
import { CameraCones } from "./CameraCones";
import { SatOverlay } from "./SatOverlay";

export const useSmall = () => {
  const q = "(max-width: 640px)";
  const [small, setSmall] = React.useState(() => window.matchMedia(q).matches);
  React.useEffect(() => {
    const m = window.matchMedia(q);
    const fn = (e) => setSmall(e.matches);
    m.addEventListener("change", fn);
    return () => m.removeEventListener("change", fn);
  }, []);
  return small;
};

const Waves = ({ color, reduce }) => (
  <span className="pointer-events-none absolute left-0 top-0 scale-[0.4] sm:scale-100" aria-hidden="true">
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className={reduce ? "absolute" : "pt-ripple absolute"}
        style={{ width: 220, height: 132, left: -110, top: -66, animationDelay: `${i * 1.1}s`, background: `radial-gradient(ellipse, transparent 52%, ${color} 68%, transparent 74%)`, opacity: reduce ? 0.4 : 0 }}
      />
    ))}
  </span>
);

const DeadZone = ({ ap, label }) => (
  <span className="relative z-10 -ml-4 -mt-4 flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-white bg-[#1F2937]/80 backdrop-blur-sm" data-testid={`villa-dead-${ap.id}`}>
    <WifiOff size={14} color="#fff" />
    <span className="absolute left-1/2 top-9 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#1F2937]/90 px-2 py-0.5 text-[10px] font-medium text-white">{label}</span>
  </span>
);

const Aura = ({ glow, reduce }) => (
  <div className="pointer-events-none absolute inset-0 mix-blend-screen" aria-hidden="true" data-testid="villa-aura">
    {glow.map(([x, y, w, h], i) => (
      <span key={i} className={reduce ? "absolute" : "pt-aura absolute"} style={{ left: `${x}%`, top: `${y}%`, width: `${w * 2}%`, height: `${h * 2}%`, transform: "translate(-50%, -50%)", animationDelay: `${i * 0.8}s`, borderRadius: "50%", background: "radial-gradient(ellipse, rgba(0,195,255,0.42), rgba(0,195,255,0.16) 45%, transparent 70%)" }} />
    ))}
  </div>
);

export const VillaImage = ({ house = "villa", on, active, onSelect, labels, names, layers = { wifi: true, cams: true, sat: true } }) => {
  const reduce = useReducedMotion();
  const SCENE = SCENES[house];
  const small = useSmall();
  return (
    <div style={{ aspectRatio: SCENE.aspect }} className="relative w-full select-none overflow-hidden rounded-3xl bg-[#0A0E13]" data-testid="villa-image">
      <img
        key={SCENE.img}
        src={SCENE.img}
        alt={house === "chalet" ? "Chalet am Berghang mit WLAN-Abdeckung" : "Moderne Villa mit WLAN-Abdeckung"}
        className="absolute inset-0 h-full w-full object-cover transition-[filter] duration-700"
        style={{ filter: on ? "none" : "grayscale(0.75) brightness(0.7)" }}
      />
      <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(9,10,12,0) 55%, rgba(9,10,12,0.35) 100%)" }} aria-hidden="true" />
      {on && layers.wifi && <Aura glow={SCENE.glow} reduce={reduce} />}
      {on && layers.cams && SCENE.cams && <CameraCones cams={SCENE.cams} size={SCENE.size} reduce={reduce} />}
      {on && layers.sat && SCENE.sat && <SatOverlay sat={SCENE.sat} size={SCENE.size} reduce={reduce} />}

      {SCENE.aps.map((ap, i) => {
        const dead = !on && SCENE.dead.includes(ap.id);
        const router = !on && ap.id === SCENE.router;
        if (!on && !dead && !router) return null;
        if (on && !layers.wifi) return null;
        const isActive = active === ap.id;
        return (
          <div key={ap.id} className="absolute" style={{ left: `${small && ap.m ? ap.m[0] : ap.x}%`, top: `${small && ap.m ? ap.m[1] : ap.y}%` }}>
            {on && <span className="pointer-events-none absolute left-1/2 top-1/2 h-12 w-20 -translate-x-1/2 -translate-y-1/2 rounded-[50%] mix-blend-screen sm:h-36 sm:w-60" style={{ background: "radial-gradient(ellipse, rgba(0,195,255,0.6), rgba(0,195,255,0.12) 50%, transparent 72%)" }} />}
            {on && <Waves color="rgba(125,225,255,0.95)" reduce={reduce} />}
            {router && <Waves color="rgba(255,122,0,0.8)" reduce={reduce} />}
            {dead ? (
              <DeadZone ap={ap} label={labels.deadzone} />
            ) : (
              <motion.button
                onClick={() => onSelect(ap.id)}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: on ? i * 0.08 : 0, type: "spring", stiffness: 300, damping: 20 }}
                className={`relative z-10 -ml-3.5 -mt-3.5 flex h-7 w-7 sm:-ml-5 sm:-mt-5 sm:h-10 sm:w-10 items-center justify-center rounded-full border shadow-[0_6px_20px_rgba(0,0,0,0.35)] backdrop-blur-md transition-colors ${isActive ? "border-[#FF7A00] bg-[#FF7A00]" : "border-white/70 bg-white/25 hover:bg-white/40"}`}
                aria-label={router ? labels.router : names[i]}
                data-testid={`villa-ap-${ap.id}`}
              >
                {router ? <Router size={17} color="#fff" /> : <Wifi size={17} color="#fff" />}
                {isActive && (
                  <span className="pointer-events-none absolute bottom-11 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-white px-3 py-1 text-xs font-medium text-[#090A0C] shadow-lg">{router ? labels.router : names[i]}</span>
                )}
              </motion.button>
            )}
          </div>
        );
      })}
    </div>
  );
};
